import {
  ErrorTagUtil, HashUtil, FluentWaitUtil, BuildReleaseLabels,
} from 'www-shared';
import ErrorUtil from './ErrorUtil';
import hekateBus from '../../bus';

export default class AntiSpamUtil {
  private static readonly tic = Date.now();

  private static firstInteraction = 0;

  public static getSignedScore(): string {
    if (!AntiSpamUtil.hasCsrfToken()) {
      // eslint-disable-next-line no-console
      console.warn('CSRF Token not set');
      return '';
    }

    const csrfToken = AntiSpamUtil.getCsrfTokenOrNull();
    if (csrfToken === null) {
      return 'null-csrf-token';
    }

    const toSign = HashUtil.generateToSign({ csrfToken });
    let sig: string;

    for (let ii = 0; ii < 100000; ii += 1) {
      sig = HashUtil.makeSalt();
      const isValid = HashUtil.validateSignedScore({ toSign, sig, timeoutSeconds: 1 });

      if (isValid) {
        const signed = `${toSign}#${sig}`;
        return signed;
      }
    }

    return 'unsigned';
  }

  public static hasCsrfToken(): boolean {
    try {
      return !!this.getCsrfTokenOrNull();
    } catch {
      return false;
    }
  }

  private static async refreshCsrfToken() {
    hekateBus.dispatchEvent('refresh-csrf-token', {});
  }

  /** Like waitForCsrfToken but ensures the token has not expired */
  public static async waitForNonExpiredCsrfToken(): Promise<string | null> {
    let token = await AntiSpamUtil.waitForCsrfToken({ isForce: false, timeoutMilliseconds: 1000 * 5 });
    if (!token) {
      token = await AntiSpamUtil.waitForCsrfToken({ isForce: true, timeoutMilliseconds: 1000 * 10 });
      if (!token) {
        return null;
      }
    }

    try {
      const expiry = 1000 * 60 * 60 * 5;
      const [, dateAdded] = token.split('-');
      const expiryTime = parseInt(dateAdded, 10) + expiry;
      // note that the user's clock may be off which could break this logic
      if (Date.now() < expiryTime) {
        // we have a non-expired token
        return token;
      }

      // eslint-disable-next-line no-console
      console.log(`Refreshing CSRF token with expiry time: ${new Date(expiryTime).toLocaleString()}`);
    } catch (e) {
    // eslint-disable-next-line no-console
      console.error('Error checking token expiry', e);
    }

    // we have a token but it is expired so try to get a new one
    return AntiSpamUtil.waitForCsrfToken({ isForce: true, timeoutMilliseconds: 1000 * 10 });
  }

  public static async waitForCsrfToken({
    isForce,
    timeoutMilliseconds,
  }: {
    isForce: boolean;
    timeoutMilliseconds: number;
  }): Promise<string | null> {
    if (isForce) {
      window.csrfToken = null; // force refresh
    }

    if (AntiSpamUtil.getCsrfTokenOrNull() !== null) {
      return AntiSpamUtil.getCsrfTokenOrNull();
    }

    // refresh the token
    AntiSpamUtil.refreshCsrfToken();

    // wait for the new token
    const maxTries = 10;
    await FluentWaitUtil.whileNotFalsy({
      provider: () => AntiSpamUtil.getCsrfTokenOrNull(),
      timeBetweenChecksInMilliseconds: timeoutMilliseconds / maxTries,
      maxTries,
    }).catch(() => {
      /** do nothing */
    });

    return AntiSpamUtil.getCsrfTokenOrNull();
  }

  public static getCsrfTokenOrNull(): string | null {
    try {
      const result = window.csrfToken || null;
      if (!result) {
        // eslint-disable-next-line no-console
        console.warn('csrfToken not set');
      }
      return result;
    } catch (e) {
      ErrorUtil.safePostMessageToSentry({
        tag: ErrorTagUtil.hekateCsrfToken,
        msg: 'Could not find csrf token ',
      });
      // eslint-disable-next-line no-console
      console.error('CSRF Token not set in AntiSpamUtil.getCsrfToken');
      return null;
    }
  }

  public static async csrfTokenTest({
    hint,
  }: {
    hint: 'form-submitter' | 'signup-422-screen';
  }) {
    const csrfToken = AntiSpamUtil.getCsrfTokenOrNull();
    const signedScore = AntiSpamUtil.getSignedScore();

    // log to csrf test endpoint
    const result = await fetch('/api/fb/csrfTokenTest', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-www-hint': hint,
        ...await AntiSpamUtil.commonFetchHeaders(),
      },
      body: JSON.stringify({
        csrfToken,
        signedScore,
        ...AntiSpamUtil.getTimings(),
      }),
    }).then((res) => res.json());

    // eslint-disable-next-line no-console
    console.log(`CSRF Test Result: ${JSON.stringify(result)}`);
    return result.isSuccess === true; // force hard boolean
  }

  /**
   * Refresh the page if the user interacts with a form on page open in a tab
   * that has been open for a long time
   *
   * Refresh the page when the user interacts, setting a nonce to avoid caching
   * Scroll to the form they were interacting with.
   */
  public static handleTabsThatHaveBeenOpenForTooLong() {
    Array.from(document.body.querySelectorAll('[form-id]')).forEach((form) => {
      ['click', 'touch'].forEach((event) => {
        form.addEventListener(event, () => {
          AntiSpamUtil.firstInteraction = Date.now();
        }, { once: true });
      });
    });

    if (window.location.search.includes('form-id')) {
      setTimeout(() => {
        const url = new URL(window.location.href);
        const formId = url.searchParams.get('form-id');
        // scroll to the form
        const form = document.querySelector(`[form-id="${formId}"]`);
        if (form) {
          const formRect = form.getBoundingClientRect();
          const scrollTop = window.scrollY || document.documentElement.scrollTop;
          const targetScrollTop = formRect.top + scrollTop - 225;
          window.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
        }
      }, 1500);
    }

    let runOnce = false;
    const setUpRefresh = () => {
      if (runOnce) {
        // eslint-disable-next-line no-console
        console.log('skipping setting up refresh event listener');
        return;
      }
      runOnce = true;

      // eslint-disable-next-line no-console
      console.log('setting up refresh event listener');
      const refreshPage = (formId: string | null) => {
        const url = new URL(window.location.href);
        url.searchParams.delete('tab-refresh');
        url.searchParams.delete('form-id');
        url.searchParams.set('form-id', `${formId}`);
        const nonce = Math.random().toString(16).substring(2, 10);
        url.searchParams.set('tab-refresh', nonce);
        window.location.href = `${url.href}`;
      };

      Array.from(document.body.querySelectorAll('[form-id]')).forEach((form) => {
        const formId = form.getAttribute('form-id');
        ['click', 'touch'].forEach((event) => {
          form.addEventListener(event, () => refreshPage(formId));
        });
      });
    };

    const timeoutMilliseconds = 1000 * 60 * 60;
    const tic = Date.now();

    // refresh the page every 6 hours adding a nonce to the url to avoid caching iff
    // the user interacts with any hekate form
    const interval = setInterval(() => {
      const toc = Date.now();
      if (toc - tic < timeoutMilliseconds) return;
      clearInterval(interval);
      setUpRefresh();
    }, 5000);

    // refresh the page every 6 hours adding a nonce to the url to avoid caching iff
    // the user interacts with any hekate form
    setTimeout(() => {
      setUpRefresh();
    }, timeoutMilliseconds);
  }

  /**
   * Get the time in seconds since the page was loaded and the time since the
   * first interaction.
   */
  public static getTimings(): { timingsPageLoad: string; timingsFirstInteraction: string } {
    try {
      const now = Date.now();
      const timingsPageLoad = Math.floor((now - AntiSpamUtil.tic) / 1000).toString(10);
      const timingsFirstInteraction = Math.floor((now - AntiSpamUtil.firstInteraction) / 1000).toString(10);
      return { timingsPageLoad, timingsFirstInteraction };
    } catch (e) {
      return { timingsPageLoad: '', timingsFirstInteraction: '' };
    }
  }

  /** Common headers for fetch requests */
  public static async commonFetchHeaders(): Promise<{ [x: string]: string }> {
    return {
      'x-www-referer': document.location.href,
      'x-www-build-tags': `hekate ${BuildReleaseLabels.PACKAGE} ${BuildReleaseLabels.COMMIT} ${BuildReleaseLabels.TIME}`,
    };
  }
}
